/**
 * Référentiel des sessions de trading affichées dans la carte de session du journal
 * (heures UTC — sans dépendance DST, exactement comme avant).
 *
 * Les valeurs par défaut sont STRICTEMENT celles historiquement codées en dur dans
 * `TradingSessionCard` : tant que l'utilisateur ne personnalise rien, l'affichage
 * du journal est identique à ce qu'il a toujours été.
 *
 * L'utilisateur peut, depuis `/settings?tab=parametres` → « Sessions de trading » :
 *   - renommer et redéfinir les bornes des sessions par défaut ;
 *   - créer des sessions supplémentaires ;
 *   - supprimer uniquement les sessions qu'il a créées.
 *
 * La logique de calcul (ouverture, barres, aiguille) n'est pas touchée : seules les
 * constantes deviennent paramétrables.
 */

/** Forme persistée dans `userPreferences.tradingSessions` (Firestore). */
export interface StoredTradingSession {
  id: string;
  /** Renseigné uniquement si l'utilisateur a renommé la session, ou s'il l'a créée. */
  name?: string;
  utcStart: number;
  utcEnd: number;
  color?: string;
}

/** Session prête à l'emploi, après fusion des personnalisations avec les valeurs par défaut. */
export interface TradingSessionDefinition {
  id: string;
  /** Nom brut. Pour une session par défaut non renommée, c'est la clé i18n qui prime. */
  name: string;
  utcStart: number;
  utcEnd: number;
  color: string;
  /** Drapeau + fuseau de la ville de référence — uniquement pour les sessions par défaut. */
  countryCode?: string;
  iana?: string;
  /** Une session par défaut est modifiable mais jamais supprimable. */
  isDefault: boolean;
  /** true → afficher `name` tel quel ; false → traduire via `calendarView.session_<name>`. */
  isCustomName: boolean;
}

interface DefaultTradingSession {
  id: string;
  name: string;
  countryCode: string;
  iana: string;
  utcStart: number;
  utcEnd: number;
  color: string;
}

/** Sessions par défaut — identiques au tableau `SESSIONS` d'origine de TradingSessionCard. */
export const DEFAULT_TRADING_SESSIONS: readonly DefaultTradingSession[] = [
  { id: 'asia',     name: 'Asia',     countryCode: 'JP', iana: 'Asia/Tokyo',         utcStart: 0,  utcEnd: 7,  color: '#a855f729' },
  { id: 'london',   name: 'London',   countryCode: 'GB', iana: 'Europe/London',      utcStart: 7,  utcEnd: 12, color: '#a5e4ff2e' },
  { id: 'new-york', name: 'New York', countryCode: 'US', iana: 'America/New_York',   utcStart: 12, utcEnd: 21, color: '#ef444429' },
] as const;

/** Au-delà, la carte flottante du journal devient illisible. */
export const MAX_TRADING_SESSIONS = 8;

/** Palette proposée pour les sessions créées par l'utilisateur. */
export const SESSION_COLOR_PALETTE = [
  '#38bdf8', '#4ade80', '#c026d3', '#3b82f6',
  '#f59e0b', '#ef4444', '#a78bfa', '#14b8a6',
];

const DEFAULT_BY_ID = new Map(DEFAULT_TRADING_SESSIONS.map(s => [s.id, s]));

/** Ramène une heure quelconque dans [0, 24[, arrondie à la minute. */
function normalizeHour(value: unknown, fallback: number): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  const rounded = Math.round(n * 60) / 60;
  return ((rounded % 24) + 24) % 24;
}

function isValidColor(value: unknown): value is string {
  return typeof value === 'string' && (
    /^#[0-9a-fA-F]{6}(?:[0-9a-fA-F]{2})?$/.test(value.trim()) ||
    /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(?:\s*,\s*(?:0|1|0?\.\d+))?\s*\)$/i.test(value.trim())
  );
}

/**
 * Fusionne les personnalisations enregistrées avec les sessions par défaut.
 * Une session par défaut absente du stockage est systématiquement réinjectée :
 * c'est ce qui garantit qu'elle ne peut pas disparaître.
 */
export function resolveTradingSessions(
  stored?: StoredTradingSession[] | null,
): TradingSessionDefinition[] {
  const resolved: TradingSessionDefinition[] = [];
  const seen = new Set<string>();

  if (Array.isArray(stored)) {
    for (const entry of stored) {
      if (!entry || typeof entry.id !== 'string' || !entry.id || seen.has(entry.id)) continue;
      const base = DEFAULT_BY_ID.get(entry.id);
      const rawName = typeof entry.name === 'string' ? entry.name.trim() : '';

      if (base) {
        seen.add(base.id);
        resolved.push({
          id: base.id,
          name: rawName || base.name,
          utcStart: normalizeHour(entry.utcStart, base.utcStart),
          utcEnd: normalizeHour(entry.utcEnd, base.utcEnd),
          color: isValidColor(entry.color) ? entry.color : base.color,
          countryCode: base.countryCode,
          iana: base.iana,
          isDefault: true,
          isCustomName: rawName !== '' && rawName !== base.name,
        });
      } else {
        if (!rawName) continue; // une session créée sans nom n'a aucun sens
        seen.add(entry.id);
        resolved.push({
          id: entry.id,
          name: rawName,
          utcStart: normalizeHour(entry.utcStart, 0),
          utcEnd: normalizeHour(entry.utcEnd, 0),
          color: isValidColor(entry.color) ? entry.color : SESSION_COLOR_PALETTE[0],
          isDefault: false,
          isCustomName: true,
        });
      }
    }
  }

  // Réinjection des sessions par défaut manquantes, à leur position d'origine.
  DEFAULT_TRADING_SESSIONS.forEach((base, index) => {
    if (seen.has(base.id)) return;
    const fallback: TradingSessionDefinition = {
      id: base.id,
      name: base.name,
      utcStart: base.utcStart,
      utcEnd: base.utcEnd,
      color: base.color,
      countryCode: base.countryCode,
      iana: base.iana,
      isDefault: true,
      isCustomName: false,
    };
    resolved.splice(Math.min(index, resolved.length), 0, fallback);
  });

  return resolved.slice(0, MAX_TRADING_SESSIONS);
}

/** Forme minimale à persister — on n'écrit le nom que s'il diffère du nom par défaut. */
export function toStoredTradingSessions(
  sessions: TradingSessionDefinition[],
): StoredTradingSession[] {
  return sessions.map(s => {
    const base = DEFAULT_BY_ID.get(s.id);
    const entry: StoredTradingSession = {
      id: s.id,
      utcStart: normalizeHour(s.utcStart, base?.utcStart ?? 0),
      utcEnd: normalizeHour(s.utcEnd, base?.utcEnd ?? 0),
      color: s.color,
    };
    if (!base || s.name !== base.name) entry.name = s.name;
    return entry;
  });
}

/** Une session par défaut est-elle revenue à ses valeurs d'origine ? */
export function isPristineDefault(session: TradingSessionDefinition): boolean {
  const base = DEFAULT_BY_ID.get(session.id);
  if (!base) return false;
  return (
    session.name === base.name &&
    session.utcStart === base.utcStart &&
    session.utcEnd === base.utcEnd &&
    session.color === base.color
  );
}

/** Session par défaut d'origine (pour le bouton « réinitialiser »). */
export function getDefaultTradingSession(id: string): TradingSessionDefinition | null {
  const base = DEFAULT_BY_ID.get(id);
  if (!base) return null;
  return {
    id: base.id,
    name: base.name,
    utcStart: base.utcStart,
    utcEnd: base.utcEnd,
    color: base.color,
    countryCode: base.countryCode,
    iana: base.iana,
    isDefault: true,
    isCustomName: false,
  };
}

/** 7.5 → "07:30" (valeur d'un `<input type="time">`). */
export function hoursToTimeInput(hours: number): string {
  const total = Math.round(normalizeHour(hours, 0) * 60);
  const h = Math.floor(total / 60) % 24;
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** "07:30" → 7.5. Renvoie null si la saisie est incomplète. */
export function timeInputToHours(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h > 23 || m > 59) return null;
  return h + m / 60;
}

/** Décale une plage UTC dans un autre fuseau, pour l'aperçu « dans mon fuseau ». */
export function shiftHoursByOffset(hours: number, offsetHours: number): number {
  return normalizeHour(hours + offsetHours, 0);
}

/** Identifiant stable pour une session créée par l'utilisateur. */
export function createCustomSessionId(): string {
  return `custom_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}
